import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import { ehEstadoDoAdmin } from '../utils/execucaoDoAdmin';

export const selectEstadoDaSimulacao = (state: RootState) => state.negociacaoSimulacao;

/** O `podeUsar` só vale para o escritório em que foi lido (CA-1). */
export const selectPodeUsarSimulacao = (state: RootState, escritorioId: string | null) =>
  escritorioId !== null &&
  state.negociacaoSimulacao.acesso.carregadoPara === escritorioId &&
  state.negociacaoSimulacao.acesso.podeUsar;

export const selectFormularioDaSimulacao = (state: RootState) =>
  state.negociacaoSimulacao.formulario;
export const selectAgenteDaSimulacao = (state: RootState) => state.negociacaoSimulacao.agente;
export const selectSimulacaoAtual = (state: RootState) => state.negociacaoSimulacao.simulacao;
export const selectEnvioDaConversa = (state: RootState) => state.negociacaoSimulacao.envio;
export const selectErroDoEncerramento = (state: RootState) =>
  state.negociacaoSimulacao.erroDoEncerramento;

export const selectInicioDaSimulacao = createSelector(selectEstadoDaSimulacao, (estado) => ({
  iniciando: estado.iniciando,
  erro: estado.erroDoInicio,
}));

export const selectAvancoDoRelogio = createSelector(selectEstadoDaSimulacao, (estado) => ({
  avancando: estado.avancando,
  pendentes: estado.pendentesDoAvanco,
  erro: estado.erroDoAvanco,
}));

/** O que a conversa mostra: o que chegou depois do último Limpar. */
export const selectEventosVisiveis = createSelector(
  [
    (state: RootState) => state.negociacaoSimulacao.eventos,
    (state: RootState) => state.negociacaoSimulacao.limpoAteSeq,
  ],
  (eventos, limpoAteSeq) => eventos.filter((evento) => evento.seq > limpoAteSeq),
);

/** A exportação ignora o Limpar: leva o histórico inteiro da simulação atual (CA-21, CA-22). */
export const selectEventosParaExportacao = (state: RootState) => state.negociacaoSimulacao.eventos;

export const selectAcessoDaSimulacao = (state: RootState) => state.negociacaoSimulacao.acesso;
export const selectAssinaturaDaTela = (state: RootState) => state.negociacaoSimulacao.assinatura;
export const selectLimpoAteSeq = (state: RootState) => state.negociacaoSimulacao.limpoAteSeq;

/**
 * O que decide o ritmo do polling (W3): 1 s enquanto a Lexi processa ou há comando em voo, 5 s no
 * resto. `carregandoEventos` evita empilhar leituras quando o core demora mais que o intervalo, e
 * a leitura final confirmada desliga o timer depois de recuperar todas as páginas do encerramento.
 */
export const selectSincronizacaoDaSimulacao = createSelector(selectEstadoDaSimulacao, (estado) => ({
  simulacaoId: estado.simulacao?.id ?? null,
  ultimoSeq: estado.ultimoSeq,
  carregandoEventos: estado.carregandoEventos,
  encerrada: estado.simulacao?.status === 'ENCERRADA',
  leituraFinalConcluida:
    estado.leituraFinalConcluida && estado.ultimoSeq >= (estado.simulacao?.ultimoSeq ?? 0),
  rapido:
    estado.simulacao?.processando === true ||
    estado.envio?.status === 'enviando' ||
    estado.avancando ||
    estado.iniciando,
}));

export const selectPaginacaoDaSimulacao = createSelector(selectEstadoDaSimulacao, (estado) => ({
  temMais: estado.temMaisEventos,
  carregando: estado.carregandoEventos,
  erro: estado.erroDosEventos,
  ultimoSeq: estado.ultimoSeq,
  exportacao: estado.exportacao,
}));

export const selectSuperficieDaSimulacao = (state: RootState) =>
  state.negociacaoSimulacao.superficie;
export const selectContextoDoAdmin = (state: RootState) => state.negociacaoSimulacao.contextoAdmin;
export const selectConfiguracaoDoAdmin = (state: RootState) =>
  state.negociacaoSimulacao.configuracaoAdmin;
export const selectExecucoesDaSimulacao = (state: RootState) => state.negociacaoSimulacao.execucoes;
export const selectPassosDaExecucao = (state: RootState) => state.negociacaoSimulacao.passos;

export const selectUltimoSeqDaSimulacao = (state: RootState) => state.negociacaoSimulacao.ultimoSeq;
export const selectMarcoDosLogs = (state: RootState) => state.negociacaoSimulacao.marcoDosLogs;

/**
 * Configuração salva depois do início: pelo estado do core (agente, que segue a revisão em que
 * começou) ou pelo 409 de um comando (fluxo antigo, que recusa a próxima ação).
 */
export const selectConfiguracaoAlteradaNaExecucao = (state: RootState): boolean => {
  const { simulacao, configuracaoAlteradaEm } = state.negociacaoSimulacao;
  if (!simulacao) return false;
  return (
    (ehEstadoDoAdmin(simulacao) && simulacao.configuracaoAlterada) ||
    configuracaoAlteradaEm === simulacao.id
  );
};
